import { Fragment } from 'react';
import { useT } from '../../../i18n/locale-context';
import type { LandingIllustration } from '../../../content/landings/types';

interface Props {
  data: LandingIllustration;
}

/**
 * Иллюстрации правой колонки на /start — снимки реального Pro-кабинета от
 * 13.08.2026 (см. комментарий у `LandingIllustration` в content/landings/types.ts).
 * Разметка и копия транскрибированы из макетов
 * `docs/superpowers/specs/2026-08-14-landings-mockups/landing-pro{,-uz}.html`
 * (классы `.mkt`, `.rangebar`, `.phone`, `.msg`, `.doc-chip`, `.steps`), цвета —
 * через m16-токены с кроссовером, принятым в остальных секциях лендинга:
 * mockup `--ink-muted` → `text-ink-soft`, `--ink-soft` → `text-ink-muted`,
 * `--bg` → `bg-bone`, `--surface` → `bg-bone-surface`, `--border` → `border-edge`,
 * `--brand` → `brand`, `--brand-soft` → `brand-light`; зелёный из `.tag-real`/
 * `.msg` — `status-active-*`.
 */
export function Illustration({ data }: Props) {
  const t = useT();

  if (data.kind === 'chat') {
    return (
      <div
        className="mx-auto grid max-w-[420px] gap-2.5 rounded-[20px] border border-edge bg-bone-surface p-4 shadow-m-md"
        aria-label={t('landing.illustration.chatLabel')}
      >
        {data.messages.map((m, i) => (
          <div
            key={i}
            className={
              m.side === 'out'
                ? 'ml-auto max-w-[92%] rounded-xl bg-brand-light px-[13px] py-2.5 text-[13.5px] leading-[1.45] text-ink'
                : 'max-w-[92%] rounded-xl border border-edge bg-bone px-[13px] py-2.5 text-[13.5px] leading-[1.45] text-ink'
            }
          >
            {m.title && <b className="mb-0.5 block">{m.title}</b>}
            {m.body}
            {m.time && <small className="text-ink-soft"> · {m.time}</small>}
          </div>
        ))}
      </div>
    );
  }

  if (data.kind === 'stats') {
    // ⚠️ Заголовок здесь крупнее, чем у 'range'/'docs': в макете этот вид
    // всегда живёт внутри блока «Что меняет Pro» (`.jtbd`), и `.jtbd h3`
    // (21–27px) перебивает `.mkt h3` (15.5px) по порядку каскада — оба
    // правила одинаковой специфичности, но `.jtbd h3` объявлено позже
    // (landing-pro.html:151 против :116). Заголовок реально переносится на
    // две строки в макете — это не опечатка авторов, а победивший каскад.
    return (
      <div className="rounded-lg border border-edge bg-bone-surface p-4 shadow-m-sm">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <h3 className="text-[clamp(21px,2.8vw,27px)] font-extrabold leading-[1.12] text-ink">
            {data.title}
          </h3>
          <span className="inline-block rounded-md bg-status-active-bg px-2 py-[3px] text-[10.5px] font-extrabold uppercase tracking-[0.05em] text-status-active-text">
            {data.badge}
          </span>
        </div>
        <div className="mb-3 text-xs text-ink-soft">{data.source}</div>
        <div>
          {data.rows.map((row) => (
            <div
              key={row.label}
              className="flex items-baseline justify-between gap-2.5 border-t border-edge py-[9px]"
            >
              <span className="text-sm text-ink">{row.label}</span>
              <span
                className={
                  row.strong
                    ? 'text-[15.5px] font-extrabold text-brand'
                    : 'text-sm font-extrabold text-ink'
                }
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[13.5px] text-ink-soft">{data.note}</p>
      </div>
    );
  }

  if (data.kind === 'range') {
    const pct = Math.min(100, Math.max(0, data.markerPct));
    return (
      <div className="rounded-lg border border-edge bg-bone-surface p-4 shadow-m-sm">
        <div className="mb-1 flex items-center gap-2">
          <h3 className="text-[15.5px] font-bold text-ink">{data.title}</h3>
          <span className="inline-block rounded-md bg-status-active-bg px-2 py-[3px] text-[10.5px] font-extrabold uppercase tracking-[0.05em] text-status-active-text">
            {data.badge}
          </span>
        </div>
        <div className="mb-3 text-xs text-ink-soft">{data.source}</div>
        <div className="grid grid-cols-2 gap-3">
          {data.cells.map((cell) => (
            <div key={cell.label}>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.07em] text-ink-soft">
                {cell.label}
              </div>
              <div className="text-[22px] font-extrabold text-ink">{cell.value}</div>
            </div>
          ))}
        </div>
        <div className="mb-1.5 mt-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-ink-soft">
          {data.rangeLabel}
        </div>
        <div
          role="img"
          aria-label={t('landing.illustration.rangeAriaLabel', {
            label: data.rangeLabel,
            min: data.min,
            max: data.max,
            pct,
          })}
          className="relative my-1.5 h-1.5 rounded-full bg-gradient-to-r from-brand-light to-brand"
        >
          <span
            aria-hidden="true"
            style={{ left: `${pct}%` }}
            className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[2.5px] border-bone-surface bg-ink shadow-m-sm"
          />
        </div>
        <div className="flex justify-between text-[12.5px] font-bold text-ink">
          <span>{data.min}</span>
          <span>{data.max}</span>
        </div>
        <p className="mt-2 text-[13.5px] text-ink-soft">
          <span aria-hidden="true" className="mr-1 font-extrabold text-ink">
            ●
          </span>
          {data.note}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="rounded-lg border border-edge bg-bone-surface p-4 shadow-m-sm">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-status-active-bg px-2.5 py-1 text-[11px] font-bold text-status-active-text">
            <span
              aria-hidden="true"
              className="h-[7px] w-[7px] rounded-full bg-status-active-dot"
            />
            {data.status}
          </span>
          <span className="text-xs text-ink-soft">{data.left}</span>
        </div>
        <div className="mb-2 mt-2.5 text-[15.5px] font-bold leading-[1.35] text-ink">
          {data.title}
        </div>
        {data.categories.map((category) => (
          <span
            key={category}
            className="mb-2.5 mr-1.5 inline-block rounded-full border border-edge bg-bone px-2.5 py-[3px] text-xs font-semibold text-ink-soft"
          >
            {category}
          </span>
        ))}
        {data.rows.map((row) => (
          <div
            key={row.label}
            className="flex items-baseline justify-between gap-2.5 border-t border-edge pt-[9px]"
          >
            <span className="text-[10.5px] font-bold uppercase tracking-[0.07em] text-ink-soft">
              {row.label}
            </span>
            <span className="text-right text-sm font-extrabold text-ink">{row.value}</span>
          </div>
        ))}
        {data.docs.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pt-1.5">
            {data.docs.map((doc) => (
              <span
                key={doc}
                className="inline-flex items-center gap-1.5 rounded-md border border-edge bg-bone px-2.5 py-1.5 text-[12.5px] font-bold text-ink"
              >
                <span className="rounded bg-[#c0442e] px-1 py-0.5 text-[9px] font-extrabold text-white">
                  PDF
                </span>
                {doc}
              </span>
            ))}
          </div>
        )}
      </div>
      {data.steps.length > 0 && (
        <div className="mt-[22px] flex flex-wrap items-center gap-2">
          {data.steps.map((step, i) => (
            <Fragment key={step}>
              <span className="rounded-full border border-edge bg-bone-surface px-3.5 py-2 text-[13.5px] font-bold shadow-m-sm">
                {step}
              </span>
              {i < data.steps.length - 1 && (
                <span aria-hidden="true" className="font-extrabold text-ink-soft">
                  →
                </span>
              )}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
